import {
  useEffect, useId, useRef, useState,
  type ReactNode, type RefObject,
} from 'react'

/**
 * A base dos gráficos: escala, marcas de eixo, geometria de barra,
 * medida de largura, dica e a moldura `Figura`.
 *
 * Nada aqui depende de biblioteca. A decisão está escrita em
 * `docs/dataviz-clinuma.md`: o Clinuma desenha retângulo, linha e
 * grade — matemática de sete linhas — e paga 0 KB por isso, contra a
 * ordem de ~100 KB gzip do Recharts sobre um pacote que hoje inteiro
 * tem 118 KB gzip.
 */

/* ══ escala ═══════════════════════════════════════════════════════ */

/** Mapa linear domínio→pixel. Domínio degenerado cai no meio da faixa. */
export const escala = (d0: number, d1: number, r0: number, r1: number) =>
  (v: number): number => (d1 === d0 ? (r0 + r1) / 2 : r0 + ((v - d0) / (d1 - d0)) * (r1 - r0))

/**
 * Marcas de eixo em números redondos (0 / 500 / 1.000 / 1.500).
 *
 * O eixo nunca começa fora do zero num gráfico de barra: cortar a
 * base multiplica visualmente a diferença entre as barras, e é o
 * jeito mais fácil de mentir sem escrever um número errado.
 */
export function marcasDeEixo(maximo: number, alvo = 4): number[] {
  if (!(maximo > 0)) return [0]
  const bruto = maximo / alvo
  const mag = Math.pow(10, Math.floor(Math.log10(bruto)))
  const passo = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((p) => p >= bruto) ?? 10 * mag
  const marcas: number[] = []
  for (let i = 0; i * passo < maximo + passo; i++) marcas.push(i * passo)
  return marcas
}

/** O topo do eixo: a última marca redonda, nunca o valor máximo cru. */
export const tetoDoEixo = (maximo: number, alvo = 4): number => {
  const m = marcasDeEixo(maximo, alvo)
  return m[m.length - 1] ?? 0
}

/* ══ geometria ════════════════════════════════════════════════════ */

/**
 * Barra que cresce para a DIREITA, com 4px de raio só na ponta e
 * canto vivo na linha de base. O canto vivo é o que ancora a barra
 * no zero; arredondar os dois lados faz a barra flutuar.
 */
export function barraH(x: number, y: number, larg: number, alt: number, r = 4): string {
  const rr = Math.max(0, Math.min(r, larg, alt / 2))
  if (larg <= 0.5) return ''
  return `M${x},${y} H${x + larg - rr} A${rr},${rr} 0 0 1 ${x + larg},${y + rr}`
    + ` V${y + alt - rr} A${rr},${rr} 0 0 1 ${x + larg - rr},${y + alt} H${x} Z`
}

/** Barra que cresce para CIMA. `y` é o topo. */
export function barraV(x: number, y: number, larg: number, alt: number, r = 4): string {
  const rr = Math.max(0, Math.min(r, alt, larg / 2))
  if (alt <= 0.5) return ''
  return `M${x},${y + alt} V${y + rr} A${rr},${rr} 0 0 1 ${x + rr},${y}`
    + ` H${x + larg - rr} A${rr},${rr} 0 0 1 ${x + larg},${y + rr} V${y + alt} Z`
}

/** Retângulo puro — segmento do meio de uma pilha. */
export const retangulo = (x: number, y: number, l: number, a: number): string =>
  a <= 0.5 ? '' : `M${x},${y} h${l} v${a} h${-l} Z`

/* ══ medida ═══════════════════════════════════════════════════════ */

/**
 * Largura real do container, em pixel.
 *
 * Um `viewBox` elástico seria menos código, mas escala o TEXTO junto:
 * o mesmo gráfico numa coluna estreita renderiza rótulo de 7px. Aqui
 * o SVG é desenhado no tamanho de verdade e a tipografia fica fixa.
 */
export function useLargura<T extends HTMLElement>(): [RefObject<T | null>, number] {
  const ref = useRef<T>(null)
  const [largura, setLargura] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver((entradas) => {
      const l = entradas[0]?.contentRect.width ?? 0
      setLargura(Math.max(0, Math.round(l)))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return [ref, largura]
}

/* ══ dica (tooltip) ═══════════════════════════════════════════════ */

export type Dica = { x: number; y: number; titulo: string; linhas: string[] } | null

/**
 * Dica de hover E de foco de teclado. A dica NUNCA é o único jeito de
 * ler um valor — todo gráfico daqui traz a tabela em `<details>`. Ela
 * acelera quem tem mouse, não desbloqueia informação.
 */
export function useDica() {
  const [dica, setDica] = useState<Dica>(null)
  return {
    dica,
    mostrar: (d: NonNullable<Dica>) => setDica(d),
    esconder: () => setDica(null),
  }
}

export function CaixaDeDica({ dica }: { dica: Dica }) {
  if (!dica) return null
  return (
    <div className="gfx__dica" role="status"
         style={{ left: dica.x, top: dica.y }}>
      <strong>{dica.titulo}</strong>
      {dica.linhas.map((l, i) => <span key={i}>{l}</span>)}
    </div>
  )
}

/* ══ moldura ══════════════════════════════════════════════════════ */

export type ItemLegenda = { rotulo: string; cor: string; risco?: boolean }
export type Tabela = { colunas: string[]; linhas: (string | number)[][] }

/**
 * Toda figura do Clinuma tem quatro partes e a quarta não é opcional:
 * título, legenda (a partir de DUAS séries), desenho e **tabela**.
 *
 * A tabela vive num `<details>` visível, não em `.oculto-visual`. Não
 * é só leitor de tela: é a recepcionista que precisa do número exato
 * para digitar no WhatsApp, e é o que faz o gráfico continuar
 * legível se a cor falhar por qualquer motivo — daltonismo, monitor
 * ruim, impressão em preto e branco.
 */
export function Figura({
  titulo, subtitulo, legenda, tabela, dica, children, acao,
}: {
  titulo: string
  subtitulo?: string | undefined
  legenda?: ItemLegenda[] | undefined
  tabela: Tabela
  dica?: Dica | undefined
  children: ReactNode
  acao?: ReactNode
}) {
  const id = useId()
  return (
    <figure className="gfx" aria-labelledby={`${id}-t`}>
      <figcaption className="gfx__cabecalho">
        <div>
          <h3 className="gfx__titulo" id={`${id}-t`}>{titulo}</h3>
          {subtitulo && <p className="gfx__sub">{subtitulo}</p>}
        </div>
        {acao}
      </figcaption>

      {/* Uma série só não ganha legenda: o título já diz o que está
          desenhado, e uma caixa com um quadradinho é repetição. */}
      {legenda && legenda.length > 1 && (
        <ul className="gfx__legenda">
          {legenda.map((l) => (
            <li key={l.rotulo}>
              <span className={l.risco ? 'gfx__chave gfx__chave--risco' : 'gfx__chave'}
                    style={{ background: l.cor }} aria-hidden="true" />
              {l.rotulo}
            </li>
          ))}
        </ul>
      )}

      <div className="gfx__area">
        {children}
        <CaixaDeDica dica={dica ?? null} />
      </div>

      <details className="gfx__tabela">
        <summary>Ver os números</summary>
        <div className="rolagem-x">
          <table>
            <thead>
              <tr>{tabela.colunas.map((c) => <th key={c} scope="col">{c}</th>)}</tr>
            </thead>
            <tbody>
              {tabela.linhas.map((l, i) => (
                <tr key={i}>
                  {l.map((c, j) => (
                    j === 0
                      ? <th key={j} scope="row">{c}</th>
                      : <td key={j}>{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  )
}

/* ══ paleta em JS ═════════════════════════════════════════════════ */

/**
 * Os quatro slots, na ordem fixa. O componente escreve
 * `var(--cl-viz-serie-N)`, então o tema é resolvido pelo CSS e não
 * existe hex no TSX.
 *
 * `corDaSerie` recebe o ÍNDICE ESTÁVEL da entidade, não a posição na
 * lista renderizada. Se a tela filtra um profissional, os outros não
 * podem trocar de cor — quem aprendeu "a Ana é a turquesa" foi
 * enganado no instante em que a Ana sumiu do filtro.
 */
export const SERIES = [
  'var(--cl-viz-serie-1)',
  'var(--cl-viz-serie-2)',
  'var(--cl-viz-serie-3)',
  'var(--cl-viz-serie-4)',
] as const

export const OUTROS = 'var(--cl-viz-outros)'

export const corDaSerie = (indiceEstavel: number): string =>
  SERIES[indiceEstavel] ?? OUTROS

/**
 * Índice estável a partir de uma lista de ids: ordena por id e
 * congela. Duas telas que chamam isto com a mesma lista pintam a
 * mesma entidade da mesma cor.
 */
export function indicesEstaveis(ids: string[]): Map<string, number> {
  const m = new Map<string, number>()
  ;[...ids].sort().forEach((id, i) => m.set(id, i))
  return m
}
